<template>
  <o-dialog
    ref="dialog"
    :title="dialogTitle"
    :info="info"
    :options="dialogOptions"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { Course, CourseRun } from '~/_api/models/course'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $notification, $date, $i18n } = useNuxtApp()

const dialog = ref<any>()
const course = ref<Course | null>(null)
const hasActiveCourses = ref(false)
const courseRuns = ref<CourseRun[]>([])

const dialogTitle = computed(() => {
  return hasActiveCourses.value
    ? $i18n.t('components.course.actions.delete.cannotDeleteTitle')
    : $i18n.t('components.course.actions.delete.deleteTitle')
})

const info = computed(() => {
  if (hasActiveCourses.value) {
    return $i18n.t('components.course.actions.delete.infoActive', {
      name: course.value?.name,
      id: course.value?.idCourse,
    })
  }

  return $i18n.t('components.course.actions.delete.infoConfirm', {
    name: (course.value as any)?.course?.name ?? course.value?.name,
    id: (course.value as any)?.course?.idCourse ?? course.value?.idCourse,
  })
})

const dialogOptions = computed(() => {
  if (hasActiveCourses.value) {
    return [
      { text: $i18n.t('general.close'), type: 'cancel' }
    ]
  }

  return [
    { text: $i18n.t('general.cancel'), type: 'cancel' },
    { text: $i18n.t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm }
  ]
})

const open = async (courseItem: Course) => {
  course.value = courseItem
  hasActiveCourses.value = false
  courseRuns.value = []

  // Check for active course runs
  try {
    const response = await $api.course.listRuns(courseItem)
    courseRuns.value = response.items || []

    // Check if any runs are active (not expired and not upcoming means active)
    const today = $date.format(new Date())

    hasActiveCourses.value = courseRuns.value.some((run: any) => {
      // Check if course run has dates
      if (!run.dates || run.dates.length === 0) { return false }

      // Get first and last dates
      const sortedDates = run.dates.toSorted((a: any, b: any) => a - b)
      const lastDate = sortedDates[sortedDates.length - 1]

      // Course is active if today is between first and last date (inclusive)
      // or if it's upcoming (hasn't started yet)
      const isExpired = lastDate < today
      const isActive = !isExpired

      // Prevent deletion if course is not expired (active or upcoming)
      return isActive
    })
  } catch (e: any) {
    console.error('Error fetching course runs:', e)
  }

  dialog.value.open()
}

const confirm = async () => {
  if (!course.value) return

  emit('update:loading', true)

  try {
    const response = await $api.course.delete(course.value)
    emit('update:loading', false)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.course.actions.delete.deletedTitle'),
        text: $i18n.t('components.course.actions.delete.deletedText', {
          name: (course.value as any)?.course?.name ?? course.value.name,
        }),
      })

      emit('success')

      return true
    }
  } catch (e: any) {
    $notification.toast({
      title: $i18n.t('components.course.actions.delete.errorDeletingTitle'),
      text: $i18n.t('components.course.actions.delete.errorDeletingText', {
        name: (course.value as any)?.course?.name ?? course.value.name,
      }),
    })

    emit('error')
  }
}

defineExpose({ open })
</script>
